import React, { useEffect, useRef, useState } from 'react';
import I18n from 'i18n-js';

import { TCIDropdown } from '@/components/ui/Dropdown';
import { TCITooltip } from '@/components/ui/Tooltip';
import AssignmentMadeTooltipContent from '@/components/common/AssignmentMadeTooltipContent';
import DeleteAssessmentButton, {
  getDeleteTooltipContent,
} from '@/components/staffer/assessments/DeleteAssessmentButton';
import ShareLink from '@/components/staffer/assessments/ShareLink';
import UnShareButton from '@/components/staffer/assessments/UnShareButton';
import { CopyModal } from '@/components/staffer/assessments/CopyLink';
import * as Routes from 'modules/routes';
import PrintModal from '@/components/shared/PrintModal';
import { AssessmentInfo, AssignmentFormAssessment } from '../types';

import styles from './AssessmentRow.module.scss';
import SourceIcon from '../SourceIcon';
import EditButton from '../EditButton';
import { userInfo } from '../../actions';
import PrintAssessmentOption from '../MoreMenu/PrintAssessmentOption';
import SaveToGoogleDriveOption from '../MoreMenu/SaveToGoogleDriveOption';
import CopyButton from '../CopyButton';
import { sourceIsMyTests } from '../utils';

interface Props {
  assessment: AssessmentInfo;
  currentLessonOrUnitPayload: { lesson_id?: string | number, unit_id?: string | number };
  onAssignClick: (
    lessonId: number,
    initialValues: { modelType: string; modelsToAssign: string; },
    assessments: AssignmentFormAssessment[] | undefined
  ) => void;
  programId: string | number;
}

const AssignmentTooltipContent = ({ isAssigned }: { isAssigned: boolean }) => {
  if (!isAssigned) return <>Create Assignment</>;

  return <AssignmentMadeTooltipContent />;
};

const AssessmentRow = ({
  assessment,
  currentLessonOrUnitPayload,
  onAssignClick,
  programId,
}: Props) => {
  const [isCopyModalOpen, setIsCopyModalOpen] = useState(false);
  const hasAssignment = assessment.assigned_by_staffer;
  const [printConfig, setPrintConfig] = useState<{ asyncUrl?: string; headerText: string; url: string } | null>(null);

  const handleAssignClick = () => {
    if (hasAssignment) return;

    let assessments;
    if (!sourceIsMyTests(assessment.source)) {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      const { id, name, lesson_id } = assessment;
      assessments = [{ id, lessonId: lesson_id, name }];
    }

    onAssignClick(assessment.lesson_id, assessment.assignment_modal_initial_value, assessments);
  };

  const previewLink = Routes.shared_program_lesson_assessment_path(programId, assessment.lesson_id, assessment.id);
  const assignmentIconClasses = hasAssignment ? `fa fa-calendar-check-o ${styles.green}` : 'fa fa-calendar';

  // The title truncates by width via CSS, so whether it's clipped depends on the
  // column/card width, not its length. Detect actual overflow so the full-name
  // tooltip only appears when the title is really cut off (and re-check on resize).
  const titleRef = useRef<HTMLDivElement>(null);
  const [isTitleTruncated, setIsTitleTruncated] = useState(false);

  useEffect(() => {
    const el = titleRef.current;
    if (!el) return undefined;

    const checkTruncation = () => {
      setIsTitleTruncated(el.scrollWidth > el.clientWidth);
    };
    checkTruncation();

    const observer = new ResizeObserver(checkTruncation);
    observer.observe(el);
    return () => observer.disconnect();
  }, [assessment.name]);

  return (
    <div className={styles.tableRow}>
      <div ref={titleRef} className={styles.titleColumn}>
        {/* AIDEV-NOTE: Title truncates by width via CSS (.titleColumn white-space:nowrap + */}
        {/* text-overflow:ellipsis + min-width:0), not a fixed character count. The full name */}
        {/* stays in the DOM for screen readers; the hover tooltip is shown only when the title */}
        {/* is actually clipped (isTitleTruncated, scrollWidth > clientWidth on the column ref). */}
        <TCITooltip disabled={!isTitleTruncated}>
          <TCITooltip.Trigger asChild>
            <a
              className="tw-bg-white"
              href={sourceIsMyTests(assessment.source)
                ? Routes.edit_staffer_assessment_path(assessment.id, { program_id: programId })
                : '#'}
              onClick={() => !sourceIsMyTests(assessment.source) && setIsCopyModalOpen(true)}
            >
              {assessment.name}
            </a>
          </TCITooltip.Trigger>
          <TCITooltip.Content>{assessment.name}</TCITooltip.Content>
        </TCITooltip>

        <CopyModal
          ajaxPath={Routes.copy_to_staffer_assessments_path({
            assessment_id: assessment.id,
            lesson_id: assessment.lesson_id,
            program_id: programId,
          })}
          closeModal={() => setIsCopyModalOpen(false)}
          modalIsOpen={isCopyModalOpen}
          statusPath={Routes.plato_api_assessment_path(':assessment_id')}
          testName={assessment.name}
        />
      </div>
      <div className={styles.locationColumn}>
        {/* AIDEV-NOTE: cellLabel spans surface the column name when rows stack into */}
        {/* single-column cards at the reflow breakpoint; hidden on the desktop table. */}
        <span className={styles.cellLabel}>{I18n.t('libraries.assessments.column_location')}</span>
        <TCITooltip>
          <TCITooltip.Trigger asChild>
            {/* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex */}
            <span tabIndex={0}>{assessment.location}</span>
          </TCITooltip.Trigger>
          <TCITooltip.Content>{assessment.unit_lesson_title}</TCITooltip.Content>
        </TCITooltip>
      </div>
      <div className={styles.categoryColumn}>
        <span className={styles.cellLabel}>{I18n.t('libraries.assessments.column_category')}</span>
        {assessment.category}
      </div>
      <div className={styles.sourceColumn}>
        <span className={styles.cellLabel}>{I18n.t('libraries.assessments.column_source')}</span>
        {assessment.source ? (
          <TCITooltip>
            <TCITooltip.Trigger asChild>
              {/* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex */}
              <div className={`w-fit ${styles.sourceImage}`} tabIndex={0}>
                <SourceIcon
                  firstName={userInfo.staffer?.first_name}
                  lastName={userInfo.staffer?.last_name}
                  source={assessment.source}
                />
              </div>
            </TCITooltip.Trigger>
            <TCITooltip.Content>{assessment.source}</TCITooltip.Content>
          </TCITooltip>
        ) : (
          <div className={`w-fit ${styles.sourceImage}`}>
            <SourceIcon
              firstName={userInfo.staffer?.first_name}
              lastName={userInfo.staffer?.last_name}
              source={assessment.source}
            />
          </div>
        )}
      </div>
      <div className={styles.questionColumn}>
        <span className={styles.cellLabel}>{I18n.t('libraries.assessments.column_num_questions')}</span>
        {assessment.question_count}
      </div>
      <div className={styles.actionColumn}>
        <EditButton
          assessment={assessment}
          programId={programId}
        />

        <TCITooltip>
          <TCITooltip.Trigger asChild>
            <a aria-label="Preview" className={styles.active} href={previewLink}>
              <i aria-hidden="true" className="fa fa-eye" />
            </a>
          </TCITooltip.Trigger>
          <TCITooltip.Content>Preview</TCITooltip.Content>
        </TCITooltip>

        <TCITooltip>
          <TCITooltip.Trigger asChild>
            <button
              aria-haspopup="dialog"
              aria-label="Create Assignment"
              className={styles.assignButton}
              type="button"
              onClick={handleAssignClick}
            >
              <i aria-hidden="true" className={assignmentIconClasses} />
              <span className="sr-only">Create Assignment</span>
            </button>
          </TCITooltip.Trigger>
          <TCITooltip.Content>
            <AssignmentTooltipContent isAssigned={hasAssignment} />
          </TCITooltip.Content>
        </TCITooltip>

        <TCIDropdown>
          <TCIDropdown.Trigger asChild>
            <button
              aria-label="More"
              className={styles.active}
              type="button"
            >
              <i aria-hidden="true" className="fa fa-ellipsis-h" />
            </button>
          </TCIDropdown.Trigger>
          {/* AIDEV-NOTE: Items wrap existing button-rendering components without `asChild` because most of */}
          {/* them return fragments (button + modal). Mouse clicks on the inner buttons still fire and the */}
          {/* surrounding TCIDropdown.Item provides menu/menuitem semantics for screen readers. Future work: */}
          {/* refactor each item component to expose its click handler so we can use Item.onSelect for full */}
          {/* keyboard activation (Enter/Space on the menuitem itself). */}
          <TCIDropdown.Content
            align="end"
            side="bottom"
            width="unset"
          >
            <TCIDropdown.Item
              onSelect={() => setPrintConfig({
                asyncUrl: Routes.new_staffer_assessment_download_path(assessment.id, { program_id: programId }),
                headerText: I18n.t('libraries.assessments.print_assessment'),
                url: Routes.pdf_staffer_assessment_print_path(assessment.id, { program_id: programId }),
              })}
            >
              <PrintAssessmentOption iconClass="fa-print" text={I18n.t('libraries.assessments.print_assessment')} />
            </TCIDropdown.Item>
            <TCIDropdown.Item
              onSelect={() => setPrintConfig({
                asyncUrl: Routes.new_staffer_assessment_download_path(
                  assessment.id,
                  { answer_key: true, program_id: programId },
                ),
                headerText: I18n.t('libraries.assessments.print_answer_key'),
                url: Routes.pdf_staffer_assessment_print_path(
                  assessment.id,
                  { answer_key: true, program_id: programId },
                ),
              })}
            >
              <PrintAssessmentOption
                iconClass="fa-check-square-o"
                text={I18n.t('libraries.assessments.print_answer_key')}
              />
            </TCIDropdown.Item>
            <TCIDropdown.Item onSelect={e => e.preventDefault()}>
              <SaveToGoogleDriveOption
                assessment={assessment}
                url={Routes.pdf_staffer_assessment_print_path(assessment.id, { program_id: programId })}
              />
            </TCIDropdown.Item>
            <TCIDropdown.Item onSelect={e => e.preventDefault()}>
              <SaveToGoogleDriveOption
                answerKey
                assessment={assessment}
                url={Routes.pdf_staffer_assessment_print_path(
                  assessment.id,
                  { answer_key: true, program_id: programId },
                )}
              />
            </TCIDropdown.Item>
            {sourceIsMyTests(assessment.source) && (
              <TCIDropdown.Item onSelect={e => e.preventDefault()}>
                <CopyButton
                  showLabel
                  assessment={assessment}
                  iconClass="fa-clone"
                  label="Copy"
                  programId={programId}
                  withTooltip={false}
                />
              </TCIDropdown.Item>
            )}
            {assessment.owned_by_current_staffer && (
              <>
                <TCIDropdown.Item asChild>
                  <a href={Routes.teacher_program_scores_path(programId, { lesson_id: assessment.lesson_id })}>
                    <i className="fa fa-bar-chart" />
                    Grades
                  </a>
                </TCIDropdown.Item>
                {/* AIDEV-NOTE: Tooltip wraps the Item (focus target during arrow nav)
                    rather than the inner button, so keyboard users see the
                    "why is this disabled" message when tabbing/arrowing through
                    the menu. Inner components receive withTooltip={false}. */}
                <TCITooltip
                  disabled={!assessment.has_been_shared && !!assessment.answerable_questions}
                >
                  <TCITooltip.Trigger asChild>
                    <TCIDropdown.Item onSelect={e => e.preventDefault()}>
                      {assessment.has_been_shared ? (
                        <UnShareButton
                          ajaxPath={Routes.unshare_staffer_assessment_path(
                            assessment.id,
                            { lesson_id: assessment.lesson_id, program_id: programId },
                          )}
                          assessment={assessment}
                          fallbackPath={Routes.library_shared_program_assessments_path(
                            programId,
                            currentLessonOrUnitPayload,
                          )}
                          shared={assessment.has_been_shared}
                        />
                      ) : (
                        <ShareLink
                          ajaxPath={Routes.share_staffer_assessment_path(
                            assessment.id,
                            { lesson_id: assessment.lesson_id, program_id: programId },
                          )}
                          button={false}
                          fallbackPath={Routes.library_shared_program_assessments_path(
                            programId,
                            currentLessonOrUnitPayload,
                          )}
                          linkText="Share"
                          model="Test"
                          paramName="assessment"
                          sharable={assessment.answerable_questions}
                          shared={assessment.has_been_shared}
                          sharedToDistrict={assessment.district_only}
                          withTooltip={false}
                        />
                      )}
                    </TCIDropdown.Item>
                  </TCITooltip.Trigger>
                  <TCITooltip.Content>
                    {assessment.has_been_shared
                      ? I18n.t('libraries.assessments.already_shared_tooltip')
                      : I18n.t('libraries.assessments.unsharable_tooltip')}
                  </TCITooltip.Content>
                </TCITooltip>
                <TCITooltip
                  disabled={!(assessment.answered_and_assigned || assessment.has_been_shared)}
                >
                  <TCITooltip.Trigger asChild>
                    <TCIDropdown.Item onSelect={e => e.preventDefault()}>
                      <DeleteAssessmentButton
                        deleteAssessmentPath={Routes.staffer_assessment_path(
                          assessment,
                          { lesson_id: assessment.lesson_id, program_id: programId },
                        )}
                        disabled={assessment.answered_and_assigned}
                        shared={assessment.has_been_shared}
                        withTooltip={false}
                      />
                    </TCIDropdown.Item>
                  </TCITooltip.Trigger>
                  <TCITooltip.Content>
                    {getDeleteTooltipContent({
                      disabled: assessment.answered_and_assigned,
                      shared: assessment.has_been_shared,
                    })}
                  </TCITooltip.Content>
                </TCITooltip>
              </>
            )}
          </TCIDropdown.Content>
        </TCIDropdown>

        <PrintModal
          asyncUrl={printConfig?.asyncUrl}
          closeModal={() => setPrintConfig(null)}
          headerText={printConfig?.headerText ?? ''}
          modalIsOpen={printConfig !== null}
          url={printConfig?.url ?? ''}
        />
      </div>
    </div>
  );
};

export default AssessmentRow;
